import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  ListSubheader,
  MenuItem,
  useTheme,
  Autocomplete,
  Alert,
  Card,
  FormHelperText,
  CircularProgress,
} from "@mui/material";
import {
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  Notes as NotesIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Inventory2 as InventoryIcon,
  SignalCellular4Bar,
  SignalCellular3Bar,
  SignalCellular2Bar,
  SignalCellular1Bar,
  PostAdd as PostAddIcon,
} from "@mui/icons-material";
import PageHeader from "../../components/PageHeader";
import { SetupCoachmark } from "../../components/selfSetup/SetupCoachmark";
import { useSelfSetupTour } from "../../hooks/useSelfSetupTour";
import { usePickupOrderStore } from "../../stores/pickup_order.store";
import { useOrganizationStore } from "../../stores/organization.store";
import { useContainerStore } from "../../stores/container.store";
import { useCollectionSiteStore } from "../../stores/collection-site.store";
import { useMaterialTypeStore } from "../../stores/recycling.store";
import { useNavigate, useLocation } from "react-router-dom";
import {
  PickupOrderAmountUnit,
  PickupOrderFillLevel,
} from "../../types/order.type";
import { MaterialTypeSchema } from "../../types/recycling.type";
import { CollectionSiteSchema } from "../../types/collection-site.type";
import { getPickupOrderCreateErrorTranslationKey } from "../../helpers/pickup-order-error.helper";

function normalizeUnitKey(raw: string): string {
  return raw.trim().toLowerCase().replace(/[\s-]+/g, "_");
}

/** Maps material type unit_of_measure to pickup order amount_unit. Returns null if unknown. */
function unitOfMeasureToPickupAmountUnit(
  raw: string | null | undefined
): PickupOrderAmountUnit | null {
  if (raw == null || typeof raw !== "string") return null;
  const v = normalizeUnitKey(raw);
  const aliases: Record<string, PickupOrderAmountUnit> = {
    kg: PickupOrderAmountUnit.KG,
    kilogram: PickupOrderAmountUnit.KG,
    kilograms: PickupOrderAmountUnit.KG,
    ton: PickupOrderAmountUnit.TONS,
    tons: PickupOrderAmountUnit.TONS,
    tonne: PickupOrderAmountUnit.TONS,
    tonnes: PickupOrderAmountUnit.TONS,
    lb: PickupOrderAmountUnit.LBS,
    lbs: PickupOrderAmountUnit.LBS,
    pound: PickupOrderAmountUnit.LBS,
    pounds: PickupOrderAmountUnit.LBS,
    bag: PickupOrderAmountUnit.BAGS,
    bags: PickupOrderAmountUnit.BAGS,
    carton: PickupOrderAmountUnit.CARTONS,
    cartons: PickupOrderAmountUnit.CARTONS,
    bottle: PickupOrderAmountUnit.BOTTLES,
    bottles: PickupOrderAmountUnit.BOTTLES,
    pallet: PickupOrderAmountUnit.PALLETS,
    pallets: PickupOrderAmountUnit.PALLETS,
    cubic_yard: PickupOrderAmountUnit.CUBIC_YARDS,
    cubic_yards: PickupOrderAmountUnit.CUBIC_YARDS,
    cubic_meter: PickupOrderAmountUnit.CUBIC_METERS,
    cubic_meters: PickupOrderAmountUnit.CUBIC_METERS,
    liter: PickupOrderAmountUnit.LITERS,
    liters: PickupOrderAmountUnit.LITERS,
    litre: PickupOrderAmountUnit.LITERS,
    litres: PickupOrderAmountUnit.LITERS,
    l: PickupOrderAmountUnit.LITERS,
    gallon: PickupOrderAmountUnit.GALLONS,
    gallons: PickupOrderAmountUnit.GALLONS,
  };
  if (aliases[v]) return aliases[v];
  const allowed = Object.values(PickupOrderAmountUnit) as string[];
  if (allowed.includes(v)) return v as PickupOrderAmountUnit;
  return null;
}

function unitLabel(t: TFunction, unit: PickupOrderAmountUnit): string {
  const key = unit as string;
  return t(`units.${key}`, { defaultValue: unit });
}

type FillLevelOption = {
  value: PickupOrderFillLevel;
  labelKey: string;
  Icon: typeof SignalCellular4Bar;
};

const FILL_LEVEL_OPTIONS: FillLevelOption[] = [
  { value: PickupOrderFillLevel.FULL, labelKey: "pickupOrders.create.fillLevels.full", Icon: SignalCellular4Bar },
  { value: PickupOrderFillLevel.THREE_QT_FULL, labelKey: "pickupOrders.create.fillLevels.threeQuarter", Icon: SignalCellular3Bar },
  { value: PickupOrderFillLevel.HALF_FULL, labelKey: "pickupOrders.create.fillLevels.half", Icon: SignalCellular2Bar },
  { value: PickupOrderFillLevel.QT_FULL, labelKey: "pickupOrders.create.fillLevels.quarter", Icon: SignalCellular1Bar },
];

const SECTION_HINTS = [
  {
    titleKey: "pickupOrders.create.detailsTitle",
    subtitleKey: "pickupOrders.create.sectionHints.details",
  },
  {
    titleKey: "pickupOrders.create.locationTitle",
    subtitleKey: "pickupOrders.create.sectionHints.location",
  },
  {
    titleKey: "pickupOrders.create.additionalTitle",
    subtitleKey: "pickupOrders.create.sectionHints.additional",
  },
] as const;

const PickupOrdersCreate: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    isStep,
    tourPhase,
    setTourPhase,
    completeStep,
    dismissTour,
  } = useSelfSetupTour();
  const tourFormActive = isStep("pickup") && tourPhase === "form";
  const tourSubmitActive = isStep("pickup") && tourPhase === "submit";
  const [hintStep, setHintStep] = useState(0);
  const [hintsActive, setHintsActive] = useState(false);
  const [guideMode, setGuideMode] = useState(false);
  const [submitHintDismissed, setSubmitHintDismissed] = useState(false);
  const submitHintRef = useRef<HTMLDivElement | null>(null);
  const sectionRefs = useRef<(HTMLElement | null)[]>([]);

  const setSectionRef = useCallback(
    (index: number) => (el: HTMLElement | null) => {
      sectionRefs.current[index] = el;
    },
    [],
  );

  const finishGuide = useCallback(() => {
    if (isStep("pickup")) {
      dismissTour();
      return;
    }
    setHintsActive(false);
    setGuideMode(false);
    setSubmitHintDismissed(true);
  }, [dismissTour, isStep]);

  const advanceHintTo = useCallback((next: number) => {
    if (next < 0 || next >= SECTION_HINTS.length) {
      setHintsActive(false);
      return;
    }
    setHintStep(next);
  }, []);

  const handleSectionNext = useCallback(() => {
    if (hintStep < SECTION_HINTS.length - 1) {
      advanceHintTo(hintStep + 1);
      return;
    }
    // Last section → spotlight Create button (no field fill required).
    setHintsActive(false);
    if (tourFormActive) {
      setTourPhase("submit");
    }
  }, [advanceHintTo, hintStep, setTourPhase, tourFormActive]);

  useEffect(() => {
    const state = location.state as { showSectionHints?: boolean } | null;
    if (!state?.showSectionHints) return;
    setHintStep(0);
    setHintsActive(true);
    setGuideMode(true);
    setSubmitHintDismissed(false);
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.state, location.pathname, navigate]);

  // Self-setup: start section tips when entering form phase.
  useEffect(() => {
    if (!tourFormActive) return;
    setHintStep(0);
    setHintsActive(true);
    setGuideMode(true);
    setSubmitHintDismissed(false);
  }, [tourFormActive]);

  // Self-setup: show submit tip when phase advances to submit.
  useEffect(() => {
    if (!tourSubmitActive) return;
    setHintsActive(false);
    setGuideMode(true);
    setSubmitHintDismissed(false);
  }, [tourSubmitActive]);

  // Create tip after section tips — do not wait for form answers.
  const showSubmitHint =
    (guideMode && !hintsActive && !submitHintDismissed) ||
    (tourSubmitActive && !submitHintDismissed);

  useEffect(() => {
    if (!hintsActive) return;
    sectionRefs.current[hintStep]?.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
  }, [hintsActive, hintStep]);

  useEffect(() => {
    if (!showSubmitHint) return;
    submitHintRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
      inline: "nearest",
    });
  }, [showSubmitHint]);

  const {
    createOrder,
    ordersLoading,
    ordersError,
    ordersErrorCode,
    clearOrdersError,
  } = usePickupOrderStore();
  const { selectedOrganization } = useOrganizationStore();
  const { containers, getContainers } = useContainerStore();
  const {
    sites,
    getCollectionSites,
    isLoading: sitesLoading,
    error: sitesError,
  } = useCollectionSiteStore();
  const { materialTypes, fetchMaterialTypes } = useMaterialTypeStore();

  /** The organization's waste types grouped by category, so the picker reads like
   *  the catalog instead of one long flat list. */
  const materialsByCategory = useMemo(() => {
    const groups = new Map<string, MaterialTypeSchema[]>();
    (materialTypes || []).forEach((material) => {
      const key = material.parent_category?.trim() || "other";
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(material);
    });
    return Array.from(groups.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([category, materials]) => ({
        category,
        materials: [...materials].sort((a, b) => a.name.localeCompare(b.name)),
      }));
  }, [materialTypes]);

  const [formData, setFormData] = useState({
    material_type_id: "",
    data: {
      pickup_address: "",
      container_id: "",
      fill_level: "" as PickupOrderFillLevel | "",
      notes: "",
      special_instructions: "",
    },
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [containerHint, setContainerHint] = useState<string | null>(null);
  const mappedErrorKey = getPickupOrderCreateErrorTranslationKey(ordersErrorCode);
  const displayedOrdersError = mappedErrorKey ? t(mappedErrorKey) : ordersError;

  const loadContainers = useCallback(async () => {
    if (!selectedOrganization) return;
    await getContainers({
      page: 1,
      limit: 100,
      organization_id: selectedOrganization.id,
    });
  }, [getContainers, selectedOrganization]);

  const loadCollectionSites = useCallback(async () => {
    if (!selectedOrganization) return;
    await getCollectionSites({
      page: 1,
      limit: 100,
      organization_id: selectedOrganization.id,
    });
  }, [getCollectionSites, selectedOrganization]);

  const loadMaterialTypes = useCallback(async () => {
    if (!selectedOrganization) return;
    await fetchMaterialTypes(selectedOrganization.id);
  }, [fetchMaterialTypes, selectedOrganization]);

  useEffect(() => {
    if (selectedOrganization) {
      loadContainers();
      loadCollectionSites();
      loadMaterialTypes();
    }
  }, [selectedOrganization, loadContainers, loadCollectionSites, loadMaterialTypes]);

  const selectedMaterial = useMemo(
    () => materialTypes.find((m) => m.id === formData.material_type_id),
    [materialTypes, formData.material_type_id]
  );

  const derivedAmountUnit = useMemo(
    () => unitOfMeasureToPickupAmountUnit(selectedMaterial?.unit_of_measure),
    [selectedMaterial?.unit_of_measure]
  );

  const unitDisplayLabel = useMemo(() => {
    if (!formData.material_type_id) {
      return t("pickupOrders.create.selectMaterialFirst");
    }
    if (!derivedAmountUnit) {
      const raw = selectedMaterial?.unit_of_measure;
      return raw
        ? t("pickupOrders.create.unrecognizedUnit", { unit: raw })
        : t("pickupOrders.create.noUnitOfMeasure");
    }
    return unitLabel(t, derivedAmountUnit);
  }, [
    formData.material_type_id,
    derivedAmountUnit,
    selectedMaterial?.unit_of_measure,
    t,
  ]);

  const containersForSite = useMemo(() => {
    const siteId = formData.data.pickup_address;
    if (!siteId) return [];
    return (containers || []).filter((c) => c.collection_site_id === siteId);
  }, [containers, formData.data.pickup_address]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!sitesLoading && !formData.data.pickup_address) {
      newErrors.pickup_address = t("pickupOrders.create.errors.collectionSiteRequired");
    }
    if (!formData.data.container_id) {
      newErrors.container_id = t("pickupOrders.create.errors.containerRequired");
    } else if (formData.data.pickup_address) {
      const ok = containersForSite.some(
        (c) => c.id === formData.data.container_id
      );
      if (!ok) {
        newErrors.container_id = t("pickupOrders.create.errors.containerMustBelongToSite");
      }
    }
    if (!formData.material_type_id) {
      newErrors.material_type_id = t("pickupOrders.create.errors.materialTypeRequired");
    } else {
      const mat = materialTypes.find((m) => m.id === formData.material_type_id);
      const category = mat?.parent_category?.trim();
      if (!category) {
        newErrors.material_type_id = t("pickupOrders.create.errors.noCategory");
      }
      const unit = unitOfMeasureToPickupAmountUnit(mat?.unit_of_measure);
      if (!unit) {
        newErrors.material_type_id =
          (newErrors.material_type_id ? newErrors.material_type_id + " " : "") +
          t("pickupOrders.create.errors.unitUndetermined");
      }
    }
    if (formData.data.container_id && !formData.data.fill_level) {
      newErrors.fill_level = t("pickupOrders.create.errors.fillLevelRequired");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearOrdersError();

    if (!validateForm()) return;
    if (!selectedOrganization) return;

    const mat = materialTypes.find((m) => m.id === formData.material_type_id)!;
    const wasteType = mat.parent_category!.trim();
    const amountUnit = unitOfMeasureToPickupAmountUnit(mat.unit_of_measure)!;

    try {
      await createOrder({
        waste_type: wasteType,
        organization_id: selectedOrganization.id,
        amount: 0,
        amount_unit: amountUnit,
        material_type_id: formData.material_type_id,
        sale_amount: 0,
        data: {
          pickup_address: formData.data.pickup_address,
          container_id: formData.data.container_id,
          fill_level: formData.data.fill_level as PickupOrderFillLevel,
          notes: formData.data.notes,
          special_instructions: formData.data.special_instructions,
        },
      });

      navigate("/pickup_orders", { state: { orderCreated: true }, replace: true });
    } catch {
      // ordersError set in store; stay on page
    }
  };

  const handleCollectionSiteChange = (
    _: React.SyntheticEvent,
    newValue: CollectionSiteSchema | null
  ) => {
    const newSiteId = newValue?.id || "";
    setContainerHint(null);
    const prevContainerId = formData.data.container_id;
    let nextContainerId = prevContainerId;
    if (newSiteId && prevContainerId) {
      const c = containers.find((x) => x.id === prevContainerId);
      if (!c || c.collection_site_id !== newSiteId) {
        nextContainerId = "";
        setContainerHint(t("pickupOrders.create.containerCleared"));
      }
    } else {
      nextContainerId = "";
    }
    setFormData({
      ...formData,
      data: {
        ...formData.data,
        pickup_address: newSiteId,
        container_id: nextContainerId,
        fill_level:
          nextContainerId && nextContainerId === prevContainerId
            ? formData.data.fill_level
            : "",
      },
    });
    setErrors((e) => ({
      ...e,
      pickup_address: "",
      container_id: "",
      fill_level: "",
    }));
  };

  if (!selectedOrganization) {
    return (
      <Box>
        <PageHeader icon={<PostAddIcon />} title={t("pickupOrders.create.title")} />
        <Alert severity="info" sx={{ mb: 3 }}>
          {t("pickupOrders.create.selectOrganization")}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 960, mx: "auto" }}>
      <PageHeader
        icon={<PostAddIcon />}
        title={t("pickupOrders.create.title")}
        subtitle={t("pickupOrders.organizationSubtitle", {
          name: selectedOrganization.name,
        })}
      />

      {displayedOrdersError && (
        <Alert
          severity="error"
          sx={{ mb: 3 }}
          onClose={clearOrdersError}
        >
          {displayedOrdersError}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <SetupCoachmark
              open={hintsActive && hintStep === 0}
              fullWidth
              title={t(SECTION_HINTS[0].titleKey)}
              subtitle={t(SECTION_HINTS[0].subtitleKey)}
              nextLabel={t("selfSetup.tourNext")}
              onNext={handleSectionNext}
              onDismiss={finishGuide}
            >
              <Card
                ref={setSectionRef(0)}
                sx={{ p: 3 }}
              >
              <Typography
                variant="h6"
                sx={{ mb: 0.5, display: "flex", alignItems: "center", gap: 1 }}
              >
                <InventoryIcon color="primary" />
                {t("pickupOrders.create.detailsTitle")}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t("pickupOrders.create.detailsDescription")}
              </Typography>
              <Grid container spacing={2} alignItems="flex-start">
                <Grid item xs={12} sm={4}>
                  <FormControl
                    fullWidth
                    required
                    error={!!errors.material_type_id}
                  >
                    <InputLabel>{t("pickupOrders.create.materialType")}</InputLabel>
                    <Select
                      value={formData.material_type_id}
                      label={t("pickupOrders.create.materialType")}
                      onChange={(e) => {
                        const id = e.target.value;
                        setFormData({ ...formData, material_type_id: id });
                        setErrors({ ...errors, material_type_id: "" });
                      }}
                    >
                      {materialsByCategory.flatMap(({ category, materials }) => [
                        <ListSubheader key={`group-${category}`}>
                          {t(`categories.${category}`, category)}
                        </ListSubheader>,
                        ...materials.map((material) => (
                          <MenuItem key={material.id} value={material.id}>
                            {material.name}
                          </MenuItem>
                        )),
                      ])}
                    </Select>
                    {errors.material_type_id && (
                      <FormHelperText>{errors.material_type_id}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label={t("pickupOrders.create.wasteCategory")}
                    value={selectedMaterial?.parent_category || ""}
                    fullWidth
                    disabled
                    placeholder="—"
                    helperText={t("pickupOrders.create.fromMaterialType")}
                    sx={{
                      "& .MuiInputBase-input.Mui-disabled": {
                        WebkitTextFillColor: theme.palette.text.secondary,
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label={t("common.unit")}
                    value={unitDisplayLabel}
                    fullWidth
                    disabled
                    placeholder={t("pickupOrders.create.selectMaterialFirst")}
                    sx={{
                      "& .MuiInputBase-input.Mui-disabled": {
                        WebkitTextFillColor: theme.palette.text.primary,
                      },
                    }}
                  />
                </Grid>
              </Grid>
              </Card>
            </SetupCoachmark>
          </Grid>

          <Grid item xs={12}>
            <SetupCoachmark
              open={hintsActive && hintStep === 1}
              fullWidth
              title={t(SECTION_HINTS[1].titleKey)}
              subtitle={t(SECTION_HINTS[1].subtitleKey)}
              nextLabel={t("selfSetup.tourNext")}
              onNext={handleSectionNext}
              onDismiss={finishGuide}
            >
              <Card
                ref={setSectionRef(1)}
                sx={{ p: 3 }}
              >
              <Typography
                variant="h6"
                sx={{ mb: 0.5, display: "flex", alignItems: "center", gap: 1 }}
              >
                <LocationIcon color="primary" />
                {t("pickupOrders.create.locationTitle")}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t("pickupOrders.create.locationDescription")}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Autocomplete
                    options={sites || []}
                    getOptionLabel={(option) =>
                      `${
                        option.data?.name || t("common.siteFallback", { id: option.id.slice(0, 8) })
                      } — ${option.data?.address?.street || t("common.noAddress")}`
                    }
                    value={
                      sites?.find(
                        (site) => site.id === formData.data.pickup_address
                      ) || null
                    }
                    loading={sitesLoading}
                    onChange={handleCollectionSiteChange}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t("pickupOrders.create.collectionSite")}
                        required
                        error={!!errors.pickup_address || !!sitesError}
                        helperText={
                          errors.pickup_address ||
                          sitesError ||
                          t("common.required")
                        }
                        placeholder={
                          sitesLoading ? t("pickupOrders.create.loadingSites") : t("pickupOrders.create.selectSite")
                        }
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {sitesLoading ? (
                                <CircularProgress color="inherit" size={20} />
                              ) : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Autocomplete
                    options={containersForSite}
                    disabled={!formData.data.pickup_address}
                    getOptionLabel={(option) =>
                      `${
                        option.data?.name ||
                        t("common.containerFallback", { id: option.id.slice(0, 8) })
                      }${
                        option.material_type?.name
                          ? ` · ${option.material_type.name}`
                          : ""
                      }`
                    }
                    value={
                      containersForSite.find(
                        (c) => c.id === formData.data.container_id
                      ) || null
                    }
                    onChange={(_, newValue) => {
                      setContainerHint(null);
                      const newId = newValue?.id || "";
                      setFormData({
                        ...formData,
                        data: {
                          ...formData.data,
                          container_id: newId,
                          fill_level: !newId
                            ? ""
                            : newId === formData.data.container_id
                              ? formData.data.fill_level
                              : "",
                        },
                      });
                      setErrors({ ...errors, container_id: "", fill_level: "" });
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t("pickupOrders.create.container")}
                        required
                        error={!!errors.container_id}
                        helperText={
                          errors.container_id ||
                          containerHint ||
                          (!formData.data.pickup_address
                            ? t("pickupOrders.create.selectCollectionSiteFirst")
                            : containersForSite.length === 0
                            ? t("pickupOrders.create.noContainersAtSite")
                            : t("common.required"))
                        }
                        placeholder={
                          formData.data.pickup_address
                            ? t("pickupOrders.create.selectContainer")
                            : "—"
                        }
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControl
                    fullWidth
                    required
                    error={!!errors.fill_level}
                    disabled={!formData.data.container_id}
                    variant="outlined"
                  >
                    <InputLabel id="fill-level-label" shrink>
                      {t("pickupOrders.create.fillLevel")}
                    </InputLabel>
                    <Select
                      labelId="fill-level-label"
                      value={formData.data.fill_level}
                      label={t("pickupOrders.create.fillLevel")}
                      displayEmpty
                      notched
                      onChange={(e) => {
                        const v = e.target.value as PickupOrderFillLevel | "";
                        setFormData({
                          ...formData,
                          data: { ...formData.data, fill_level: v },
                        });
                        setErrors({ ...errors, fill_level: "" });
                      }}
                      renderValue={(selected) => {
                        if (!selected) {
                          return (
                            <Typography
                              component="span"
                              variant="body1"
                              color="text.secondary"
                              sx={{ lineHeight: 1.4375 }}
                            >
                              {t("pickupOrders.create.selectFillLevel")}
                            </Typography>
                          );
                        }
                        const opt = FILL_LEVEL_OPTIONS.find((o) => o.value === selected);
                        if (!opt) return String(selected);
                        const Icon = opt.Icon;
                        return (
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Icon fontSize="small" color="primary" />
                            {t(opt.labelKey)}
                          </Box>
                        );
                      }}
                    >
                      <MenuItem value="" disabled>
                        <Typography variant="body2" color="text.secondary">
                          {t("pickupOrders.create.chooseLevel")}
                        </Typography>
                      </MenuItem>
                      {FILL_LEVEL_OPTIONS.map(({ value, labelKey, Icon }) => (
                        <MenuItem key={value} value={value}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                            <Icon fontSize="small" color="primary" />
                            {t(labelKey)}
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.fill_level ? (
                      <FormHelperText>{errors.fill_level}</FormHelperText>
                    ) : (
                      <FormHelperText>
                        {formData.data.container_id
                          ? t("pickupOrders.create.howFull")
                          : t("pickupOrders.create.selectContainerFirst")}
                      </FormHelperText>
                    )}
                  </FormControl>
                </Grid>
              </Grid>
              </Card>
            </SetupCoachmark>
          </Grid>

          <Grid item xs={12}>
            <SetupCoachmark
              open={hintsActive && hintStep === 2}
              fullWidth
              title={t(SECTION_HINTS[2].titleKey)}
              subtitle={t(SECTION_HINTS[2].subtitleKey)}
              nextLabel={t("selfSetup.tourNext")}
              onNext={handleSectionNext}
              onDismiss={finishGuide}
            >
              <Card
                ref={setSectionRef(2)}
                sx={{ p: 3 }}
              >
              <Typography
                variant="h6"
                sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}
              >
                <NotesIcon color="primary" />
                {t("pickupOrders.create.additionalTitle")}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    label={t("common.organization")}
                    value={selectedOrganization.name}
                    fullWidth
                    disabled
                    sx={{
                      "& .MuiInputBase-input.Mui-disabled": {
                        WebkitTextFillColor: theme.palette.text.primary,
                      },
                    }}
                    InputProps={{
                      startAdornment: (
                        <BusinessIcon
                          color="action"
                          sx={{ mr: 1, ml: -0.5 }}
                        />
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label={t("pickupOrders.create.specialInstructions")}
                    value={formData.data.special_instructions}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        data: {
                          ...formData.data,
                          special_instructions: e.target.value,
                        },
                      })
                    }
                    fullWidth
                    multiline
                    minRows={2}
                    placeholder={t("pickupOrders.create.specialInstructionsPlaceholder")}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label={t("common.notes")}
                    value={formData.data.notes}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        data: { ...formData.data, notes: e.target.value },
                      })
                    }
                    fullWidth
                    multiline
                    minRows={2}
                    placeholder={t("pickupOrders.create.notesPlaceholder")}
                  />
                </Grid>
              </Grid>
              </Card>
            </SetupCoachmark>
          </Grid>

          <Grid item xs={12}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 2,
                pt: 2,
                borderTop: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Button
                variant="outlined"
                onClick={() => navigate("/pickup_orders")}
                startIcon={<CancelIcon />}
                disabled={ordersLoading}
                sx={{ borderRadius: "8px", px: 3 }}
              >
                {t("common.cancel")}
              </Button>
              <SetupCoachmark
                open={showSubmitHint}
                title={t("pickupOrders.create.sectionHints.submitTitle")}
                subtitle={t("pickupOrders.create.sectionHints.submitSubtitle")}
                nextLabel={t("selfSetup.tourDone")}
                onNext={() => {
                  if (isStep("pickup")) {
                    completeStep("pickup");
                    return;
                  }
                  setSubmitHintDismissed(true);
                }}
                onDismiss={() => {
                  if (isStep("pickup")) {
                    dismissTour();
                    return;
                  }
                  setSubmitHintDismissed(true);
                }}
              >
                <Box ref={submitHintRef} sx={{ display: "inline-flex" }}>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={ordersLoading || sitesLoading}
                    startIcon={
                      ordersLoading ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : (
                        <SaveIcon />
                      )
                    }
                    sx={{
                      backgroundColor: theme.palette.primary.main,
                      "&:hover": {
                        backgroundColor: theme.palette.primary.dark,
                      },
                      borderRadius: "8px",
                      px: 3,
                    }}
                  >
                    {ordersLoading
                      ? t("pickupOrders.create.creating")
                      : t("pickupOrders.create.submit")}
                  </Button>
                </Box>
              </SetupCoachmark>
            </Box>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
};

export default PickupOrdersCreate;
